// =============================================================================
// Bridge to the iOS home-screen widget (WidgetKit).
//
// On native builds the app pushes a small JSON snapshot of "today" into the
// shared App Group container via the EuleaWidgetBridge Capacitor plugin
// (ios/App/App/EuleaWidgetBridge.swift); the SwiftUI widget renders it and
// the plugin asks WidgetKit to refresh. On the web this module is a no-op.
//
// The snapshot is deliberately tiny and display-ready: the widget process
// must not re-implement recurrence/priority logic.
// =============================================================================

import { Capacitor, registerPlugin } from '@capacitor/core';
import { format, addDays, startOfDay, startOfWeek, isSameMonth } from 'date-fns';
import { expandEvents } from '../utils/recurrence';
import { defaultData } from '../utils/storage';
import { priorityRank, priorityEmoji } from './priorities';
import { getDateLocale, translate, weekdayLabels } from './i18n';
import { currentTheme } from './theme';

const Bridge = Capacitor.isNativePlatform()
  ? registerPlugin('EuleaWidgetBridge')
  : null;

const byTimeThenPriority = (a, b) => {
  const ta = a.time || '';
  const tb = b.time || '';
  if (ta !== tb) {
    if (!ta) return 1;
    if (!tb) return -1;
    return ta.localeCompare(tb);
  }
  return priorityRank(a.priority) - priorityRank(b.priority);
};

// ---------------------------------------------------------------------------
// Week grids per calendar, for the home-screen calendar widget. The widget
// draws exactly what it is given — month label, seven day cells and stacked
// event bars — so the layout logic (and therefore the look) stays here,
// beside the app's own calendar rules.
//
// We ship a *range* of weeks rather than just the current one: the widget
// process picks the week containing its own idea of "today". Otherwise the
// week (and the highlighted day) would freeze at whatever moment the app last
// pushed, and the widget would keep circling yesterday after midnight.
// ---------------------------------------------------------------------------
const WEEKS_BACK = 6;
const WEEKS_AHEAD = 6;

const colorsFor = (data, sharedId) => {
  const all = data.calendar?.categories || [];
  const scoped = all.filter((c) => (c.sharedCalendarId || null) === sharedId);
  const byId = new Map(scoped.map((c) => [c.id, c]));
  const base = defaultData.calendar.categories.map((d) => byId.get(d.id) || d);
  const map = new Map(base.map((c) => [c.id, c.color]));
  return (id) => map.get(id) || base[0].color;
};

// Every dated thing this calendar shows, as {title, start, end, color}, over
// the window the widget can display. Mirrors the app's own derivation so the
// widget and the calendar page can't disagree.
const itemsForCalendar = (data, sharedId, from, to) => {
  const color = colorsFor(data, sharedId);
  const scoped = (data.calendar?.events || []).filter(
    (ev) => (ev.sharedCalendarId || null) === sharedId
  );
  const items = expandEvents(scoped, from, to).map((ev) => ({
    title: ev.title || '',
    start: ev.date,
    end: ev.endDate || ev.date,
    color: color(ev.categoryId)
  }));

  // Birthdays, if this calendar links them in.
  const bs = sharedId
    ? (() => {
        const c = (data.calendar?.sharedCalendars || []).find((x) => x.id === sharedId);
        return c && c.hasBirthdays && c.birthdayLinked !== false
          ? { linked: true, categoryId: c.birthdayCategoryId || 'purple' }
          : { linked: false };
      })()
    : (() => {
        const b = data.calendar?.birthdaySettings || {};
        return { linked: !!b.linkedToCalendar, categoryId: b.categoryId || 'purple' };
      })();
  if (bs.linked) {
    const years = new Set([from.getFullYear(), to.getFullYear()]);
    for (const p of (data.calendar?.birthdays || []).filter(
      (b) => (b.sharedCalendarId || null) === sharedId
    )) {
      let day = p.day;
      let month = p.month;
      if (!day && p.birthDate) {
        const parts = String(p.birthDate).split('-').map(Number);
        month = parts[1];
        day = parts[2];
      }
      if (!day || !month) continue;
      // The window can straddle New Year, so emit the birthday for each year
      // it touches and let the range filter below drop the rest.
      for (const year of years) {
        const d = format(new Date(year, month - 1, day), 'yyyy-MM-dd');
        items.push({ title: p.name, start: d, end: d, color: color(bs.categoryId) });
      }
    }
  }

  // Linked to-dos.
  const todoSettings = data.todo?.calendarSettings || {};
  const todoSource = sharedId
    ? [...(data.todo?.tasks || []), ...(data.todo?.sharedTasks || [])].filter(
        (t) => t.sharedCalendarId === sharedId
      )
    : todoSettings.linkedToCalendar && !todoSettings.sharedCalendarId
    ? (data.todo?.tasks || []).filter((t) => !t.sharedCalendarId)
    : [];
  for (const t of todoSource) {
    if (!t.date) continue;
    items.push({
      title: t.title || '',
      start: t.date,
      end: t.date,
      color: color(t.calendarCategoryId || todoSettings.categoryId || 'blue')
    });
  }

  return items;
};

// One week: seven day cells plus the event bars clipped to it and stacked into
// rows, longest first (the same rule the month grid uses for multi-day bars).
const buildWeek = (weekStart, items, locale) => {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const dayKeys = days.map((d) => format(d, 'yyyy-MM-dd'));
  const first = dayKeys[0];
  const last = dayKeys[6];

  // The month a week "belongs to" is the one holding most of its days, which
  // is the same as the month of its Thursday.
  const anchor = days[3];

  const bars = items
    .filter((it) => it.start <= last && it.end >= first)
    .map((it) => {
      const startCol = Math.max(0, dayKeys.indexOf(it.start < first ? first : it.start));
      const endIdx = dayKeys.indexOf(it.end > last ? last : it.end);
      return {
        title: it.title,
        color: it.color,
        startCol,
        endCol: endIdx < 0 ? startCol : endIdx,
        continuesLeft: it.start < first,
        continuesRight: it.end > last
      };
    })
    .sort((a, b) => {
      const len = b.endCol - b.startCol - (a.endCol - a.startCol);
      return len !== 0 ? len : a.startCol - b.startCol;
    });

  const rowEnds = [];
  const placed = [];
  for (const bar of bars) {
    let row = 0;
    while (rowEnds[row] !== undefined && rowEnds[row] >= bar.startCol) row++;
    if (row > 2) continue; // the widget has room for three rows
    rowEnds[row] = bar.endCol;
    placed.push({ ...bar, row });
  }

  return {
    start: first,
    end: last,
    month: format(anchor, 'MMMM', { locale }),
    monthKey: format(anchor, 'yyyy-MM'),
    days: days.map((d, i) => ({
      key: dayKeys[i],
      label: format(d, 'd'),
      weekend: d.getDay() === 0 || d.getDay() === 6,
      inMonth: isSameMonth(d, anchor)
    })),
    bars: placed
  };
};

const weeksForCalendar = (data, cal, now) => {
  const sharedId = cal.id === 'personal' ? null : cal.id;
  const thisWeek = startOfWeek(now, { weekStartsOn: 1 });
  const from = addDays(thisWeek, -7 * WEEKS_BACK);
  const to = addDays(thisWeek, 7 * WEEKS_AHEAD + 6);
  const items = itemsForCalendar(data, sharedId, from, to);
  const locale = getDateLocale();

  const weeks = [];
  for (let i = -WEEKS_BACK; i <= WEEKS_AHEAD; i++) {
    weeks.push(buildWeek(addDays(thisWeek, i * 7), items, locale));
  }
  return { id: cal.id, name: cal.name, weeks };
};

const calendarsForWidget = (data) => {
  const enabled = data.settings?.enabledFeatures || {};
  const out = [];
  if (enabled.calendar !== false && data.settings?.showPersonalCalendar !== false) {
    out.push({ id: 'personal', name: translate('todo.personalCalendar') });
  }
  for (const c of data.calendar?.sharedCalendars || []) {
    if (enabled[`shared:${c.id}`] === false) continue;
    out.push({ id: c.id, name: c.name });
  }
  return out;
};

// Derive today's events (all calendars incl. shared) + due/overdue open tasks.
export const buildWidgetSnapshot = (data) => {
  const now = new Date();
  const todayStr = format(now, 'yyyy-MM-dd');

  const events = expandEvents(
    data.calendar?.events || [],
    startOfDay(now),
    addDays(startOfDay(now), 1)
  )
    .filter((ev) => {
      const end = ev.endDate || ev.date;
      return ev.date <= todayStr && end >= todayStr;
    })
    .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''))
    .slice(0, 12) // the large "Heute" widget shows up to 12 rows
    .map((ev) => ({ title: ev.title || '', time: ev.startTime || '' }));

  const openTasks = (data.todo?.tasks || []).filter((t) => !t.done);
  const dueToday = openTasks
    .filter((t) => t.date && t.date <= todayStr)
    .sort(byTimeThenPriority)
    .slice(0, 12) // the large "Heute" widget shows up to 12 rows
    .map((t) => ({
      title: t.title || '',
      time: t.time || '',
      emoji: priorityEmoji(t.priority)
    }));

  return {
    updatedAt: now.toISOString(),
    todayEvents: events,
    todos: dueToday,
    openCount: openTasks.length,
    weekdays: weekdayLabels(),
    calendars: calendarsForWidget(data).map((cal) => weeksForCalendar(data, cal, now)),
    // The widget process has no access to the app's dictionary, so the few
    // strings it renders itself travel with the data.
    strings: {
      empty: translate('widget.openApp', 'Eulea öffnen, um den Kalender zu laden.')
    },
    // Dark mode is an in-app setting, so the widget has to be told which way
    // to paint rather than following iOS. `accent` is the palette the user
    // picked, so today's circle matches the app.
    theme: currentTheme()
  };
};

let lastJson = null;
let timer = null;

// Debounced push — called from App.js whenever `data` changes.
export const pushWidgetSnapshot = (data) => {
  if (!Bridge || !data) return;
  if (timer) clearTimeout(timer);
  timer = setTimeout(async () => {
    try {
      const json = JSON.stringify(buildWidgetSnapshot(data));
      if (json === lastJson) return;
      lastJson = json;
      await Bridge.setSnapshot({ json });
    } catch {
      // Widget updates are best-effort; never disturb the app.
    }
  }, 800);
};
